import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Button,
    FilePicker,
    Icon,
    Menus,
    Modal,
    Tabs,
    Text,
    Tooltip,
    useTheme,
} from '@Sage/hoi-poi-ui';
import { CodeEditor, useHorizontalResize } from '@Sage/ssm-web5';

import HtmlPreviewFrame from './HtmlPreviewFrame';

import './styles.scss';

const HTML_ACCEPT = ['.html', 'text/html'];
const HISTORY_LIMIT = 15;

const VIEW_MODES = [
    { key: 'split', label: 'Split screen', icon: 'switch' },
    { key: 'code', label: 'Code only', icon: 'edit' },
    { key: 'desktop', label: 'Desktop preview', icon: 'visibility' },
    { key: 'mobile', label: 'Mobile preview', icon: 'mobileDevice' },
];

const readHtmlFile = async (file) => {
    if (!file) return '';
    if (typeof file.text === 'function') {
        return file.text();
    }

    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result || '');
        reader.onerror = () => reject(new Error('Unable to read HTML file.'));
        reader.readAsText(file);
    });
};

const HtmlEditorOverlay = memo(
    ({
        campaignId,
        initialHtml = '',
        initialTitle = '',
        isOpen = false,
        onPreviewAndTest,
        onSave,
        onExitWithoutSaving,
    }) => {
        const theme = useTheme();
        const previewInputRef = useRef(null);
        const splitContainerRef = useRef(null);
        const codePaneRef = useRef(null);
        const dragCounterRef = useRef(0);
        const historyEntriesRef = useRef([initialHtml]);
        const historyIndexRef = useRef(0);

        const [draftTitle, setDraftTitle] = useState(initialTitle);
        const [draftHtml, setDraftHtml] = useState(initialHtml);
        const [historyEntries, setHistoryEntries] = useState([initialHtml]);
        const [historyIndex, setHistoryIndex] = useState(0);
        const [activeViewMode, setActiveViewMode] = useState('split');
        const [splitPaneWidth, setSplitPaneWidth] = useState(50);
        const [isSaving, setIsSaving] = useState(false);
        const [isDraggingFile, setIsDraggingFile] = useState(false);
        const [importError, setImportError] = useState('');
        const [saveError, setSaveError] = useState('');
        const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
        const [previewModalView, setPreviewModalView] = useState('desktop');

        useEffect(() => {
            historyEntriesRef.current = historyEntries;
        }, [historyEntries]);

        useEffect(() => {
            historyIndexRef.current = historyIndex;
        }, [historyIndex]);

        useEffect(() => {
            if (!isOpen) {
                setIsPreviewModalOpen(false);
                return;
            }

            historyEntriesRef.current = [initialHtml];
            historyIndexRef.current = 0;
            setDraftTitle(initialTitle);
            setDraftHtml(initialHtml);
            setHistoryEntries([initialHtml]);
            setHistoryIndex(0);
            setActiveViewMode('split');
            setSplitPaneWidth(50);
            setIsDraggingFile(false);
            setImportError('');
            setSaveError('');
        }, [initialHtml, initialTitle, isOpen]);

        useHorizontalResize({
            targetRef: codePaneRef,
            containerRef: splitContainerRef,
            minWidth: 360,
            minPercentage: 30,
            maxPercentage: 70,
            onResize: ({ percentageList }) => {
                setSplitPaneWidth(percentageList);
            },
            enabled: isOpen && activeViewMode === 'split',
        });

        const canUndo = historyIndex > 0;
        const canRedo = historyIndex < historyEntries.length - 1;
        const hasUnsavedChanges = draftHtml !== initialHtml;
        const hasHtmlContent = !!draftHtml.trim();

        const commitHtmlChange = useCallback((nextHtml) => {
            const normalizedHtml = nextHtml || '';
            const currentHtml = historyEntriesRef.current[historyIndexRef.current] || '';

            if (normalizedHtml === currentHtml) {
                setDraftHtml(normalizedHtml);
                return;
            }

            const nextEntries = [
                ...historyEntriesRef.current.slice(0, historyIndexRef.current + 1),
                normalizedHtml,
            ].slice(-HISTORY_LIMIT);

            const nextIndex = nextEntries.length - 1;
            historyEntriesRef.current = nextEntries;
            historyIndexRef.current = nextIndex;
            setHistoryEntries(nextEntries);
            setHistoryIndex(nextIndex);
            setDraftHtml(normalizedHtml);
            setImportError('');
            setSaveError('');
        }, []);

        const applyHistoryPosition = useCallback((nextIndex) => {
            const nextHtml = historyEntriesRef.current[nextIndex] || '';
            historyIndexRef.current = nextIndex;
            setHistoryIndex(nextIndex);
            setDraftHtml(nextHtml);
            setImportError('');
            setSaveError('');
        }, []);

        const handleUndo = useCallback(() => {
            if (!canUndo) return;
            applyHistoryPosition(historyIndexRef.current - 1);
        }, [applyHistoryPosition, canUndo]);

        const handleRedo = useCallback(() => {
            if (!canRedo) return;
            applyHistoryPosition(historyIndexRef.current + 1);
        }, [applyHistoryPosition, canRedo]);

        const importHtmlFile = useCallback(
            async (file) => {
                if (!file) return;

                const isHtmlFile =
                    file.type === 'text/html' || file.name?.toLowerCase().endsWith('.html');

                if (!isHtmlFile) {
                    setImportError('Only HTML files are supported.');
                    return;
                }

                try {
                    const content = await readHtmlFile(file);
                    commitHtmlChange(content);
                } catch (error) {
                    console.error(error);
                    setImportError('Unable to import the selected HTML file.');
                }
            },
            [commitHtmlChange],
        );

        const handleFileDrop = useCallback(
            async (files = []) => {
                const [file] = files;
                dragCounterRef.current = 0;
                setIsDraggingFile(false);
                await importHtmlFile(file);
            },
            [importHtmlFile],
        );

        const handleHiddenInputChange = useCallback(
            async (event) => {
                const [file] = Array.from(event.target.files || []);
                await importHtmlFile(file);
                event.target.value = '';
            },
            [importHtmlFile],
        );

        const openFileSelector = useCallback(() => {
            previewInputRef.current?.click?.();
        }, []);

        const handlePreviewDragEnter = useCallback((event) => {
            event.preventDefault();
            event.stopPropagation();
            dragCounterRef.current += 1;
            setIsDraggingFile(true);
        }, []);

        const handlePreviewDragOver = useCallback((event) => {
            event.preventDefault();
            event.stopPropagation();
        }, []);

        const handlePreviewDragLeave = useCallback((event) => {
            event.preventDefault();
            event.stopPropagation();
            dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
            if (dragCounterRef.current === 0) {
                setIsDraggingFile(false);
            }
        }, []);

        const handlePreviewDrop = useCallback(
            async (event) => {
                event.preventDefault();
                event.stopPropagation();
                const files = Array.from(event.dataTransfer?.files || []);
                await handleFileDrop(files);
            },
            [handleFileDrop],
        );

        const handleSave = useCallback(async () => {
            if (!onSave) return;

            setIsSaving(true);
            setSaveError('');

            try {
                await onSave({
                    html: draftHtml,
                    title: draftTitle,
                });
            } catch (error) {
                console.error(error);
                setSaveError('Unable to save the current HTML draft.');
            } finally {
                setIsSaving(false);
            }
        }, [draftHtml, draftTitle, onSave]);

        const handleSaveAndExit = useCallback(async () => {
            if (!onSave) return;

            setIsSaving(true);
            setSaveError('');

            try {
                await onSave({
                    html: draftHtml,
                    title: draftTitle,
                });
                onExitWithoutSaving?.(false);
            } catch (error) {
                console.error(error);
                setSaveError('Unable to save the current HTML draft.');
            } finally {
                setIsSaving(false);
            }
        }, [draftHtml, draftTitle, onExitWithoutSaving, onSave]);

        const handleExitWithoutSaving = useCallback(() => {
            onExitWithoutSaving?.(true);
        }, [onExitWithoutSaving]);

        const handleOpenPreviewModal = useCallback(() => {
            onPreviewAndTest?.({
                html: draftHtml,
                title: draftTitle,
            });
            setIsPreviewModalOpen(true);
        }, [draftHtml, draftTitle, onPreviewAndTest]);

        useEffect(() => {
            if (!isOpen) return undefined;

            const handleKeyDown = (event) => {
                const isModifierPressed = event.ctrlKey || event.metaKey;
                const isUndoShortcut =
                    (isModifierPressed && !event.shiftKey && event.key.toLowerCase() === 'z') ||
                    (!isModifierPressed && event.altKey && event.key === 'Backspace');
                const isRedoShortcut =
                    (event.ctrlKey && event.key.toLowerCase() === 'y') ||
                    (isModifierPressed && event.shiftKey && event.key.toLowerCase() === 'z');

                if (isUndoShortcut) {
                    event.preventDefault();
                    handleUndo();
                }

                if (isRedoShortcut) {
                    event.preventDefault();
                    handleRedo();
                }
            };

            window.addEventListener('keydown', handleKeyDown, true);

            return () => {
                window.removeEventListener('keydown', handleKeyDown, true);
            };
        }, [handleRedo, handleUndo, isOpen]);

        const menuItems = useMemo(
            () => [
                {
                    title: 'Save',
                    onClick: handleSave,
                },
                {
                    divider: true,
                },
                {
                    title: 'Exit without saving',
                    onClick: handleExitWithoutSaving,
                },
            ],
            [handleExitWithoutSaving, handleSave],
        );

        const previewContent = useMemo(() => {
            if (!hasHtmlContent) {
                return (
                    <div className="marketing-html-editor__empty-preview">
                        <FilePicker
                            title="Drag and Drop HTML file or attach"
                            subtitle="Drag and drop your HTML file into this area to import the design."
                            buttonLabel="Select file"
                            files={[]}
                            accept={HTML_ACCEPT}
                            maxFiles={1}
                            isFullWidth
                            onDrop={handleFileDrop}
                        />
                        {importError && (
                            <Text
                                type="caption"
                                className="marketing-html-editor__error-copy"
                                color={theme.colors.semantic.negative500}
                            >
                                {importError}
                            </Text>
                        )}
                    </div>
                );
            }

            return (
                <div
                    className="marketing-html-editor__preview-dropzone"
                    onDragEnter={handlePreviewDragEnter}
                    onDragOver={handlePreviewDragOver}
                    onDragLeave={handlePreviewDragLeave}
                    onDrop={handlePreviewDrop}
                >
                    <div className="marketing-html-editor__preview-toolbar">
                        <Text type="caption" color={theme.colors.textLight.secondary}>
                            Drag and drop a new HTML file here to replace the current one.
                        </Text>
                        <Button type="secondary" size="small" onClick={openFileSelector}>
                            Replace HTML
                        </Button>
                    </div>
                    <div className="marketing-html-editor__preview-scroll">
                        <div
                            className={[
                                'marketing-html-editor__preview-surface',
                                activeViewMode === 'mobile'
                                    ? 'marketing-html-editor__preview-surface--mobile'
                                    : '',
                            ].join(' ')}
                        >
                            <HtmlPreviewFrame
                                className="marketing-html-editor__iframe"
                                html={draftHtml}
                                title={draftTitle || 'Campaign HTML preview'}
                            />
                        </div>
                    </div>
                    {isDraggingFile && (
                        <div className="marketing-html-editor__drag-overlay">
                            <Icon name="mobileDevice" size="large" />
                            <Text type="subtitle" medium>
                                Drop HTML to replace the current content
                            </Text>
                        </div>
                    )}
                </div>
            );
        }, [
            activeViewMode,
            draftHtml,
            draftTitle,
            handleFileDrop,
            handlePreviewDragEnter,
            handlePreviewDragLeave,
            handlePreviewDragOver,
            handlePreviewDrop,
            hasHtmlContent,
            importError,
            isDraggingFile,
            openFileSelector,
            theme.colors.semantic.negative500,
            theme.colors.textLight.secondary,
        ]);

        const previewTabs = useMemo(
            () => [
                {
                    key: 'desktop',
                    title: 'Desktop preview',
                    content: (
                        <div className="marketing-html-editor__modal-preview">
                            <div className="marketing-html-editor__preview-surface">
                                <HtmlPreviewFrame
                                    className="marketing-html-editor__iframe"
                                    html={draftHtml}
                                    title={draftTitle || 'Campaign HTML desktop preview'}
                                />
                            </div>
                        </div>
                    ),
                },
                {
                    key: 'mobile',
                    title: 'Mobile preview',
                    content: (
                        <div className="marketing-html-editor__modal-preview">
                            <div className="marketing-html-editor__preview-surface marketing-html-editor__preview-surface--mobile">
                                <HtmlPreviewFrame
                                    className="marketing-html-editor__iframe"
                                    html={draftHtml}
                                    title={draftTitle || 'Campaign HTML mobile preview'}
                                />
                            </div>
                        </div>
                    ),
                },
            ],
            [draftHtml, draftTitle],
        );

        return (
            <>
                <div
                    className={[
                        'marketing-html-editor',
                        isOpen ? 'marketing-html-editor--open' : 'marketing-html-editor--hidden',
                    ].join(' ')}
                >
                    <input
                        ref={previewInputRef}
                        type="file"
                        accept=".html,text/html"
                        className="marketing-html-editor__file-input"
                        onChange={handleHiddenInputChange}
                    />
                    <div className="marketing-html-editor__topbar">
                        <div className="marketing-html-editor__topbar-left">
                            {!!draftTitle?.trim() && (
                                <Text type="h5" className="marketing-html-editor__campaign-title">
                                    {draftTitle}
                                </Text>
                            )}
                            <div className="marketing-html-editor__history-actions">
                                <Tooltip content="Undo">
                                    <button
                                        type="button"
                                        className="marketing-html-editor__icon-button"
                                        onClick={handleUndo}
                                        disabled={!canUndo}
                                    >
                                        <Icon name="reply" />
                                    </button>
                                </Tooltip>
                                <Tooltip content="Redo">
                                    <button
                                        type="button"
                                        className="marketing-html-editor__icon-button marketing-html-editor__icon-button--redo"
                                        onClick={handleRedo}
                                        disabled={!canRedo}
                                    >
                                        <Icon name="replyAll" />
                                    </button>
                                </Tooltip>
                            </div>
                            <Text
                                type="caption"
                                className="marketing-html-editor__changes-status"
                                color={
                                    hasUnsavedChanges
                                        ? theme.colors.actionMajor[500]
                                        : theme.colors.textLight.secondary
                                }
                            >
                                {hasUnsavedChanges ? 'Unsaved changes' : 'No unsaved changes'}
                            </Text>
                        </div>
                        <div className="marketing-html-editor__topbar-right">
                            <div className="marketing-html-editor__view-modes">
                                {VIEW_MODES.map(({ key, label, icon }) => (
                                    <Tooltip key={key} content={label}>
                                        <button
                                            type="button"
                                            className={[
                                                'marketing-html-editor__view-mode-button',
                                                activeViewMode === key
                                                    ? 'marketing-html-editor__view-mode-button--active'
                                                    : '',
                                            ].join(' ')}
                                            onClick={() => setActiveViewMode(key)}
                                            aria-label={label}
                                        >
                                            <Icon name={icon} />
                                        </button>
                                    </Tooltip>
                                ))}
                            </div>
                            <Button type="secondary" size="small" onClick={handleOpenPreviewModal}>
                                Preview & Test
                            </Button>
                            <Button size="small" onClick={handleSaveAndExit} isLoading={isSaving}>
                                Save and exit
                            </Button>
                            <Menus
                                size="small"
                                placement="bottom-end"
                                items={menuItems}
                                icon={<Icon name="moreHoriz" size="large" />}
                            />
                        </div>
                    </div>

                    {(saveError || importError) && (
                        <div className="marketing-html-editor__messages">
                            {saveError && (
                                <Text
                                    type="caption"
                                    className="marketing-html-editor__error-copy"
                                    color={theme.colors.semantic.negative500}
                                >
                                    {saveError}
                                </Text>
                            )}
                            {importError && hasHtmlContent && (
                                <Text
                                    type="caption"
                                    className="marketing-html-editor__error-copy"
                                    color={theme.colors.semantic.negative500}
                                >
                                    {importError}
                                </Text>
                            )}
                        </div>
                    )}

                    <div className="marketing-html-editor__body">
                        {activeViewMode === 'split' && (
                            <div
                                ref={splitContainerRef}
                                className="marketing-html-editor__split-layout"
                            >
                                <div
                                    ref={codePaneRef}
                                    className="marketing-html-editor__code-pane"
                                    style={{ width: `${splitPaneWidth}%` }}
                                >
                                    <CodeEditor
                                        className="marketing-html-editor__editor"
                                        language="html"
                                        value={draftHtml}
                                        onChange={commitHtmlChange}
                                    />
                                </div>
                                <div className="marketing-html-editor__preview-pane">
                                    {previewContent}
                                </div>
                            </div>
                        )}

                        {activeViewMode === 'code' && (
                            <div className="marketing-html-editor__single-pane">
                                <CodeEditor
                                    className="marketing-html-editor__editor"
                                    language="html"
                                    value={draftHtml}
                                    onChange={commitHtmlChange}
                                />
                            </div>
                        )}

                        {(activeViewMode === 'desktop' || activeViewMode === 'mobile') && (
                            <div className="marketing-html-editor__single-pane marketing-html-editor__single-pane--preview">
                                {previewContent}
                            </div>
                        )}
                    </div>
                </div>

                <Modal
                    isOpen={isPreviewModalOpen}
                    title="Preview & Test"
                    size="huge"
                    width="90vw"
                    height="85vh"
                    useAutoWidth={false}
                    useAutoHeight={false}
                    useContentStaticHeight={false}
                    onCancel={() => setIsPreviewModalOpen(false)}
                    onRequestClose={() => setIsPreviewModalOpen(false)}
                    cancelText="Close"
                >
                    <div className="marketing-html-editor__modal-content">
                        <Tabs
                            tabs={previewTabs}
                            activeKey={previewModalView}
                            onChange={setPreviewModalView}
                        />
                    </div>
                </Modal>
            </>
        );
    },
);

HtmlEditorOverlay.displayName = 'HtmlEditorOverlay';

HtmlEditorOverlay.propTypes = {
    campaignId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    initialHtml: PropTypes.string,
    initialTitle: PropTypes.string,
    isOpen: PropTypes.bool,
    onPreviewAndTest: PropTypes.func,
    onSave: PropTypes.func,
    onExitWithoutSaving: PropTypes.func,
};

export default HtmlEditorOverlay;
